import { useState, useEffect } from "react";
import Stepper from "../../components/Stepper/Stepper";
import Step1Form from "./Step1Form";
import Step2Form from "./Step2Form";
import Step3Form from "./Step3Form";
import Step4Form from "./Step4Form";

const initialData = {
  step1: {
    nombre: "",
    apellidoPaterno: "",
    apellidoMaterno: "",
    whatsapp: "",
    correo: "",
    estado: "",
    municipio: "",
    cp: "",
    taller: "",
  },
  step2: {
    "buy-before": "",
    "work-fiber": "",
    "work-type": {
      "Producción / Fabriación": false,
      Reparación: false,
      "Forrado / Estética": false,
      "No la he usado": false,
    },
    sector: {
      "Automotriz / Motociclismo": false,
      Industrial: false,
      Otros: false,
    },
  },
  step3: {
    url: "",
    images: [],
    "social-networks": {
      "Tik tok": false,
      Instagram: false,
      Facebook: false,
      Otra: false,
    },
  },
  step4: { projects: "", comment: "" },
};

const Form = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem("formData");
    return saved ? JSON.parse(saved) : initialData;
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    localStorage.setItem("formData", JSON.stringify(formData));
  }, [formData]);

  const handleStepClick = (stepId = step + 1) => setStep(stepId);

  const handleChange = (stepKey, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [stepKey]: { ...prev[stepKey], [field]: value },
    }));
  };

  // ✅ Special handler for step3
  const handleStep3Change = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      step3: { ...prev.step3, [field]: value },
    }));
  };

  const stepKeys = ["step1", "step2", "step3", "step4"];
  const currentStepKey = stepKeys[step - 1];

  const checkStepValidity = (stepKey) => {
    const data = formData[stepKey];
    if (stepKey === "step1") {
      return Object.values(data).every((v) => v.trim?.() !== "");
    }
    if (stepKey === "step2") {
      const radiosOk = data["buy-before"] !== "" && data["work-fiber"] !== "";
      const workTypeOk = Object.values(data["work-type"]).some(Boolean);
      const sectorOk = Object.values(data.sector).some(Boolean);
      return radiosOk && workTypeOk && sectorOk;
    }
    if (stepKey === "step4") {
      return formData.step4.projects.trim() !== "";
    }
    return true; // step3 = optional
  };

  const checkAllValid = () =>
    checkStepValidity("step1") &&
    checkStepValidity("step2") &&
    checkStepValidity("step3") &&
    checkStepValidity("step4");

  const isCurrentValid =
    step < 4 ? checkStepValidity(currentStepKey) : checkAllValid();

  const handleNext = async () => {
    if (!isCurrentValid) return;
    if (step < 4) {
      handleStepClick();
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (step === 4 && checkAllValid()) {
      setSubmitting(true);
      try {
        await fetch("/api/google", {
          method: "POST",
          body: JSON.stringify(formData),
          headers: { "Content-Type": "application/json" },
          mode: "no-cors", // 👈 makes it opaque (can't read response)
        });

        // ✅ Don't try to read or parse the response here
        console.log("Form sent successfully (no-cors mode)");
        setSubmitted(true);
        localStorage.removeItem("formData");
      } catch (err) {
        console.error("Error:", err);
        alert("Error sending form: " + err.message);
      } finally {
        setSubmitting(false);
      }
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center bg-primary-green p-6 rounded-xl text-center">
        <h2 className="text-white text-2xl font-bold mb-3">
          ¡Listo! Gracias por enviar tu información.
        </h2>
        <p className="text-white mb-2">
          Revisaremos tus respuestas y te contactaremos por WhatsApp con
          información sobre tu registro.
        </p>
        <p className="text-white mb-6">
          Si tienes dudas o alguna necesidad específica, escribe directamente al
          área de ventas.
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => window.location.reload()}
            className="bg-gray-800 text-white px-4 py-2 rounded-lg font-bold"
          >
            Inicio
          </button>
          <a
            href="https://api.whatsapp.com/send?phone=+524611495564&text=Hola,%20acabo%20de%20registrarme%20en%20su%20p%C3%A1gina.%20%E2%9C%85%20Mi%20nombre%20es"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-primary-green px-4 py-2 rounded-lg font-bold"
          >
            Contactar a ventas
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col px-4">
      <Stepper currentStep={step} onStepClick={handleStepClick} />
      {step === 1 && (
        <Step1Form
          data={formData.step1}
          onChange={(f, v) => handleChange("step1", f, v)}
        />
      )}
      {step === 2 && (
        <Step2Form
          data={formData.step2}
          onChange={(f, v) => handleChange("step2", f, v)}
        />
      )}
      {step === 3 && (
        <Step3Form data={formData.step3} onChange={handleStep3Change} />
      )}
      {step === 4 && (
        <Step4Form
          data={formData.step4}
          onChange={(f, v) => handleChange("step4", f, v)}
        />
      )}

      {step === 4 && !isCurrentValid && (
        <p className="text-red-500 text-sm mt-2">
          Por favor, completa los campos obligatorios
        </p>
      )}

      <button
        disabled={!isCurrentValid || submitting}
        className={`w-fit mt-2 self-end p-2 rounded-2xl font-bold text-xl transition ${
          isCurrentValid && !submitting
            ? "bg-primary-green text-white hover:bg-gray-700"
            : "bg-gray-500 text-gray-300 cursor-not-allowed"
        }`}
        onClick={handleNext}
      >
        {submitting ? "Enviando..." : step < 4 ? "Siguiente" : "Enviar"}
      </button>
    </div>
  );
};

export default Form;
